/**
 * Life Blueprint V2.9.9
 * Production-only Batch07 authoritative reference adapter.
 *
 * This module intentionally does NOT change the historical Batch07
 * Golden Master implementation or its partial reference fixtures.
 *
 * Production references:
 *   B82-B84  -> complete M02 DB_藏干 / DB_藏干_展开
 *   B85-B88  -> complete M02 DB_十神
 *   B89      -> complete M02 DB_60甲子
 *   B90      -> complete M02 DB_空亡规则 deterministic implementation
 *
 * Existing audited B39/B48/B71 formula functions are reused verbatim.
 */

import {
  computeB39,
  computeB48,
  computeB71,
} from "./batch07_sheet27";

import {
  CANG_GAN_TYPE,
  cangGanLookupBy,
} from "./m02_batch02_reference";

import {
  shiShenLookupBy,
} from "./m02_batch03_reference";

import {
  liuShiJiaZiLookup,
} from "./m02_batch04_reference";

import {
  composeSheet27Row82CangGan,
  composeSheet27Row83CangGanWuXing,
  composeSheet27Row84QuanZhong,
  computeSheet27B90,
} from "./m02_batch06_reference";

function requireString(name, value) {
  if (typeof value !== "string") {
    throw new TypeError(
      `[ProductionBatch07] ${name} must be a string, got ${typeof value}`
    );
  }
  return value;
}

/**
 * Excel IFERROR(VLOOKUP(...),"") semantics over a COMPLETE authoritative
 * reference table. Only MISSING_REFERENCE_KEY is converted to "".
 * Programming/type errors still fail closed.
 */
function iferrorMissingKeyEmpty(fn) {
  try {
    return fn();
  } catch (e) {
    if (e && e.code === "MISSING_REFERENCE_KEY") return "";
    throw e;
  }
}

function hiddenStemBySlot(branch, slotIndex) {
  requireString("branch", branch);

  const types = [
    CANG_GAN_TYPE.BEN_QI,
    CANG_GAN_TYPE.ZHONG_QI,
    CANG_GAN_TYPE.YU_QI,
  ];

  const type = types[slotIndex];

  return iferrorMissingKeyEmpty(() =>
    cangGanLookupBy(branch, type).cangGan
  );
}

function tenGodIferror(dayStem, targetStem) {
  requireString("dayStem", dayStem);
  requireString("targetStem", targetStem);

  // Blank hidden-stem slot ultimately produces an unmatched outer
  // VLOOKUP and therefore Excel IFERROR(...,"").
  if (targetStem === "") return "";

  return iferrorMissingKeyEmpty(() =>
    shiShenLookupBy(dayStem, targetStem)
  );
}

/**
 * Production Batch07 aggregate.
 * Input/output field names intentionally match computeBatch07Sheet27().
 */
export function computeBatch07Sheet27Production(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[ProductionBatch07] slice must be an object");
  }

  const B39 =
    slice.upstreamValue_F20 !== undefined
      ? computeB39(slice.upstreamValue_F20)
      : (() => {
          requireString(
            "upstreamValue_B39 (fallback)",
            slice.upstreamValue_B39
          );
          return slice.upstreamValue_B39;
        })();

  const B48 =
    slice.upstreamRange_F19_F30 !== undefined
      ? computeB48(slice.upstreamRange_F19_F30)
      : (() => {
          requireString(
            "upstreamValue_B48 (fallback)",
            slice.upstreamValue_B48
          );
          return slice.upstreamValue_B48;
        })();

  const B71 = computeB71(slice.upstreamC32, B39, B48);

  requireString("upstreamValue_B37", slice.upstreamValue_B37);
  requireString("upstreamValue_B52", slice.upstreamValue_B52);
  requireString("upstreamValue_B36", slice.upstreamValue_B36);
  requireString("upstreamValue_B38", slice.upstreamValue_B38);

  // Complete M02 hidden-stem composition.
  const B82 = composeSheet27Row82CangGan(slice.upstreamValue_B37);
  const B83 = composeSheet27Row83CangGanWuXing(slice.upstreamValue_B37);
  const B84 = composeSheet27Row84QuanZhong(slice.upstreamValue_B37);

  // Complete M02 ten-god matrix.
  const B85 = tenGodIferror(
    slice.upstreamValue_B52,
    slice.upstreamValue_B36
  );

  const hidden1 = hiddenStemBySlot(slice.upstreamValue_B37, 0);
  const hidden2 = hiddenStemBySlot(slice.upstreamValue_B37, 1);
  const hidden3 = hiddenStemBySlot(slice.upstreamValue_B37, 2);

  const B86 = tenGodIferror(slice.upstreamValue_B52, hidden1);
  const B87 = tenGodIferror(slice.upstreamValue_B52, hidden2);
  const B88 = tenGodIferror(slice.upstreamValue_B52, hidden3);

  // Excel IFERROR semantics preserved for B89.
  const B89 = iferrorMissingKeyEmpty(() =>
    liuShiJiaZiLookup(slice.upstreamValue_B38)
  );

  // Complete audited Sheet47 rule implementation.
  const B90 = computeSheet27B90({
    b37: slice.upstreamValue_B37,
    b51: slice.upstreamValue_B51,
  });

  return Object.freeze({
    B39,
    B48,
    B71,
    B82,
    B83,
    B84,
    B85,
    B86,
    B87,
    B88,
    B89,
    B90,

    _provenance: Object.freeze({
      adapter: "engine/v299/productionBatch07Adapter",
      sourceStatus: "AUTHORITATIVE_DATASET_COMPLETE",
      historicalPartialBatch07Modified: false,
      references: Object.freeze({
        B82_B84: "M02 DB_藏干 / DB_藏干_展开",
        B85_B88: "M02 DB_十神",
        B89: "M02 DB_60甲子",
        B90: "M02 DB_空亡规则",
      }),
    }),
  });
}
